import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "react-query";
import { useHistory } from "react-router";

import { SearchOrganizationInput } from "@secondcloset/web-components";

import { UsergroupAddOutlined } from "@ant-design/icons";
import { Button, Space } from "antd";
import { fetchOrganizationIndex } from "../../../../api/accounts/organization";
import { useDebounce } from "../../../../components/ratecard/hooks";
import { useURLStateV2 } from "../../../../hooks/application/useURLStateV2";

import { OrganizationsTable } from "./OrganizationsTable/OrganizationsTable";

type OrgOption = { label: string; value: string };

const PAGE_SIZE = 15;

const OrganizationIndexPage = () => {
  const history = useHistory();
  const [pageUrlState, setPageUrlState] = useURLStateV2({
    page: 1,
  });
  const [searchUrlState, setSearchUrlState] = useURLStateV2(
    {
      search: "",
    },
    { parseNumbers: false }
  );

  const [isCsvReportGeneratorVisible, setIsCsvReportGeneratorVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState<[React.Key, string][]>([]);
  const [excludedItems, setExcludedItems] = useState<React.Key[]>([]);
  const [areAllItemsSelected, setAreAllItemsSelected] = useState<boolean>(false);
  const [isSavingCSVReport, setIsSavingCSVReport] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption>();
  const debouncedSearch = useDebounce(searchUrlState.search ?? "", 500);

  const queryOptions = useMemo(() => {
    return {
      field: "created_at",
      direction: "desc",
      per_page: PAGE_SIZE,
      page: pageUrlState.page,
      q: selectedOrg?.label,
    };
  }, [pageUrlState, selectedOrg]);

  const organizationIndex = useQuery(["organizationIndex", queryOptions], () => fetchOrganizationIndex(queryOptions), {
    keepPreviousData: true,
  });

  const organizationLookup = useQuery(
    ["organizationLookup", debouncedSearch],
    () => fetchOrganizationIndex({ q: String(debouncedSearch).trim() }),
    {
      enabled: String(debouncedSearch).trim().length >= 3,
      keepPreviousData: true,
    }
  );

  const buildSearchBar = () => {
    return (
      <SearchOrganizationInput<OrgOption>
        mode={undefined}
        showSearch
        placeholder="Search for organization"
        searchValue={searchUrlState.search}
        value={selectedOrg}
        loading={organizationLookup.isLoading}
        organizations={organizationLookup.data ?? []}
        onSearch={(search) => setSearchUrlState((state) => ({ ...state, search }))}
        onClear={() => setSearchUrlState((state) => ({ ...state, search: "" }))}
        onInputKeyDown={(event) => {
          if (event.key.toLowerCase() === "enter") {
            setSelectedOrg({ label: event.currentTarget.value, value: event.currentTarget.value });
            setPageUrlState((state) => ({ ...state, page: 1 }));
          }
        }}
        onChange={(org) => {
          history.push(`/organizations/${org.value}`);
        }}
      />
    );
  };

  const newResult: Array<[React.Key, string]> =
    organizationIndex?.data?.map((record: { id: React.Key; name: string }) => [record.id, record.name]) || [];

  useEffect(() => {
    if (areAllItemsSelected) {
      const newlySelectedItems = [...selectedItems, ...newResult];

      // Make array of selected items have unique values.
      const uniqueItems: [React.Key, string][] = Object.values(
        newlySelectedItems.reduce((previousValue: { [key: string]: [React.Key, string] }, currentValue) => {
          previousValue[JSON.stringify(currentValue)] = currentValue;
          return previousValue;
        }, {})
      );
      setSelectedItems(uniqueItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationIndex.data, areAllItemsSelected]);

  useEffect(() => {
    if (!areAllItemsSelected) {
      setSelectedItems([]);
      setExcludedItems([]);
    }
  }, [areAllItemsSelected]);

  const buildTable = () => (
    <OrganizationsTable
      isSavingCSVReport={isSavingCSVReport}
      organizationIndex={organizationIndex}
      isCsvReportGeneratorVisible={isCsvReportGeneratorVisible}
      areAllItemsSelected={areAllItemsSelected}
      setSelectedItems={setSelectedItems}
      setAreAllItemsSelected={setAreAllItemsSelected}
      newResult={newResult}
      selectedItems={selectedItems}
      excludedItems={excludedItems}
      urlState={pageUrlState}
      setUrlState={setPageUrlState}
      setExcludedItems={setExcludedItems}
      PAGE_SIZE={PAGE_SIZE}
    />
  );

  const today = React.useRef(new Date()).current;

  return (
    <div id="organization-index-page" className="page-container">
      <Helmet title="Sierra - Organizations" />
      <div className="page-header">
        <div className="page-title">Organizations</div>
        <Space size="middle">
          <Button icon={<UsergroupAddOutlined />} onClick={() => history.push("/organizations/create")}>
            Create Organization
          </Button>
        </Space>
      </div>
      {buildSearchBar()}
      {buildTable()}
    </div>
  );
};

export default OrganizationIndexPage;
